/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { memo, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { formatGoogleAddress, IllusDelivery_1, IllusEmpty_1 } from '@sg/garnish';

import { Location } from '@order/features/locations';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useCustomer } from '../../../../../../context/Customer';
import { LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH } from '../../../../machine';
import { LocationResults } from '../../../LocationResults';
import { LocationResultsListInfoMessage } from '../../../LocationResultsListInfoMessage';
import {
  LocationSearchAddressesList,
  LocationSearchPredictionAddressesList,
} from '../../../LocationSearchAddressesList';
import { LocationSearchDeliveryResultWithoutNearbyStore } from './components';

import type {
  AddressDetails,
  AddressWithoutIds,
  LocationSearchService,
  LocationSearchMachineContext,
} from '../../../../machine';
import type { LocationSearchLocationsResults } from '../LocationSearchLocationsResults';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchDeliveryResult = memo((props: LocationSearchDeliveryResultProps) => {
  const {
    locationSearchService,
    deliverySearchString,
    deliveryLocation,
    predictionDeliveryAddresses,
    focusedLocationId,
    isLoadingPredictions,
    isLoadingLocation,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    withSafeAreaInsets,
    onDeliveryLocationCardPress,
    onPredictionPress,
    onRadarPredictionPress,
    onCustomerAddressPress,
    onTryNewAddressBtnPress,
    onChangeToPickupSearchTypeBtnPress,
    onLocationResultsExpandCollapse,
  } = props;

  const { formatMessage } = useIntl();
  const { customer } = useCustomer();
  const { addresses: customerAddresses = [] } = customer;

  // ─── Derived Data ────────────────────────────────────────────────────

  const customerDeliveryAddresses = useMemo(() => {
    return customerAddresses.map((customerAddress): AddressDetails => {
      const {
        googlePlaceId,
        id: addressId,
        latitude,
        longitude,
        street,
        zipCode,
        city,
        state,
        country,
        name,
      } = customerAddress;

      const formattedAddress = formatGoogleAddress(customerAddress);

      return {
        googlePlaceId,
        addressId,
        name: name || '',
        formattedAddress,
        latitude,
        longitude,
        street,
        zipCode,
        city,
        state,
        country,
      };
    });
  }, [customerAddresses]);

  const deliveryLocations = useMemo(
    () => (deliveryLocation ? [deliveryLocation] : []),
    [deliveryLocation],
  );

  // ─── Flags ───────────────────────────────────────────────────────────────────

  const shouldUseRadarForDelivery = useFeatureFlag(
    'cels-4823-enable-radar-delivery-address-predictions',
  );

  const hasEnteredRequiredCharsForAutocomplete =
    deliverySearchString.length >= LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH;
  const hasCustomerAddresses = customerDeliveryAddresses.length > 0;
  const hasNoDeliveryLocation = !isLoadingLocation && !deliveryLocation;
  const hasPredictions = predictionDeliveryAddresses.length > 0;

  const shouldRenderCustomerAddresses =
    hasNoDeliveryLocation && !hasEnteredRequiredCharsForAutocomplete && hasCustomerAddresses;
  const shouldRenderPredictions =
    hasNoDeliveryLocation && hasEnteredRequiredCharsForAutocomplete && hasPredictions;
  const shouldRenderNoPredictionsView =
    hasNoDeliveryLocation &&
    hasEnteredRequiredCharsForAutocomplete &&
    !isLoadingPredictions &&
    !hasPredictions;
  const shouldRenderIntroMessage = hasNoDeliveryLocation && !isLoadingPredictions;

  // ─────────────────────────────────────────────────────────────────────

  if (shouldRenderCustomerAddresses) {
    return (
      <LocationSearchAddressesList
        addresses={customerDeliveryAddresses}
        onPress={onCustomerAddressPress}
      />
    );
  }

  if (shouldRenderPredictions) {
    if (shouldUseRadarForDelivery) {
      return (
        <Location.SearchPredictionAddressesList
          addresses={predictionDeliveryAddresses as any}
          onPress={onRadarPredictionPress}
        />
      );
    }

    return (
      <LocationSearchPredictionAddressesList
        addresses={predictionDeliveryAddresses}
        onPress={onPredictionPress}
      />
    );
  }

  if (shouldRenderNoPredictionsView) {
    return (
      // @ts-expect-error TS(2741): Property 'illustrationAriaLabel' is missing in typ... Remove this comment to see the full error message
      <LocationResultsListInfoMessage
        illustration={IllusEmpty_1}
        title={formatMessage(messages.locationNoAutocompleteResult)}
        text={formatMessage(messages.locationTryAnotherSearch)}
      />
    );
  }

  if (shouldRenderIntroMessage) {
    return (
      // @ts-expect-error TS(2741): Property 'illustrationAriaLabel' is missing in typ... Remove this comment to see the full error message
      <LocationResultsListInfoMessage
        illustration={IllusDelivery_1}
        text={formatMessage(messages.locationEnterAddress)}
      />
    );
  }

  // If the searched address is too vague, it may not include a zip code,
  // which makes it impossible for us to deliver to that location.
  // In such cases, we want to prevent the user from proceeding.
  if (deliveryLocation && !deliveryLocation?.zipCode) {
    return (
      // @ts-expect-error TS(2741): Property 'illustrationAriaLabel' is missing in typ... Remove this comment to see the full error message
      <LocationResultsListInfoMessage
        illustration={IllusEmpty_1}
        title={formatMessage(messages.locationDeliveryZipCodeNotAvailableTitle)}
        text={formatMessage(messages.locationDeliveryZipCodeNotAvailableBody)}
      />
    );
  }

  if (deliveryLocation && !deliveryLocation?.restaurantSlug) {
    return (
      <LocationSearchDeliveryResultWithoutNearbyStore
        deliveryLocation={deliveryLocation}
        onTryNewAddressBtnPress={onTryNewAddressBtnPress}
        onChangeToPickupSearchTypeBtnPress={onChangeToPickupSearchTypeBtnPress}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <LocationResults
      locationSearchService={locationSearchService}
      activePinLocationId={focusedLocationId}
      interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
      interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      locationSearchType="delivery"
      locations={deliveryLocations as any}
      shouldHideDetailsModal
      isLoading={isLoadingLocation}
      isUsingSearchField={false}
      onCardPress={onDeliveryLocationCardPress}
      onLocationResultsExpandCollapse={onLocationResultsExpandCollapse}
      withSafeAreaInsets={withSafeAreaInsets}
    />
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationNoAutocompleteResult: {
    defaultMessage: `Ah snap peas! We couldn't find any locations.`,
    description: 'Location | No Autocomplete Result',
  },
  locationTryAnotherSearch: {
    defaultMessage: 'Try another search',
    description: 'Location | Try Another Search',
  },
  locationEnterAddress: {
    defaultMessage: 'Enter an address above to get started.',
    description: 'Location | Enter Address',
  },
  locationDeliveryZipCodeNotAvailableTitle: {
    defaultMessage: 'Sorry, the address you provided is not valid.',
    description: 'Location | Delivery | Zip Code Not Available Title',
  },
  locationDeliveryZipCodeNotAvailableBody: {
    defaultMessage:
      'Please make sure to include the complete street address, including the zip code',
    description: 'Location | Delivery | Zip Code Not Available Body',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchDeliveryResultProps = {
  locationSearchService: LocationSearchService; // prettier-ignore
  deliverySearchString: LocationSearchMachineContext['deliverySearchString'];
  deliveryLocation: LocationSearchMachineContext['deliveryLocation'];
  predictionDeliveryAddresses: LocationSearchMachineContext['deliveryAddressPredictions'];
  focusedLocationId?: string;
  isLoadingPredictions: boolean;
  isLoadingLocation: boolean;
  interactedLocationRestaurantSlug: string | undefined;
  interactedLocationDeliveryAddressId: string | undefined;
  withSafeAreaInsets?: boolean;
  onDeliveryLocationCardPress:
    | ((props: { restaurantId?: string; restaurantSlug?: string; restaurantName?: string }) => void)
    | (() => void);
  onCustomerAddressPress: (addressDetails: AddressDetails) => void;
  onPredictionPress: (placeId: string, placeAddress: string) => void;
  onRadarPredictionPress: (predictedAddress: AddressWithoutIds) => void;
  onTryNewAddressBtnPress: () => void;
  onChangeToPickupSearchTypeBtnPress: () => void;
  onLocationResultsExpandCollapse: ComponentProps<
    typeof LocationSearchLocationsResults
  >['onLocationResultsExpandCollapse'];
};
